import React, { useState } from 'react';

import moment from 'moment';

import { 
  createStyles, 
  makeStyles,
  Theme,
 } from '@material-ui/core';
 import CircularProgress from '@material-ui/core/CircularProgress';

 import {
  Column,
  IntegratedPaging,
  PagingState,
  TableColumnWidthInfo,
} from '@devexpress/dx-react-grid';

 import {
  Grid,
  PagingPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
} from '@devexpress/dx-react-grid-material-ui';

import ReportHeaderRow from '../components/ReportHeaderRow';
import { IReportServiceProp, withReportService } from '../withReportService';
import { SalesReportService } from './sales.report.service';
import { ISalesDisplayRecord, ISalesRecord } from './SalesReportDefinition';
import SalesToolbar, { ISalesActionDates } from './SalesToolbar';

import { useErrorHandler } from '../ReportsErrorHandler';

interface IProps extends IReportServiceProp<SalesReportService> {
}

const SalesReportBase: React.FC<IProps> = (props) => {
  // tslint:disable-next-line:no-console
  // console.log('SalesReportBase: ', props);
  const defaultFilterDates: ISalesActionDates = {
    startDate: moment().date(1).toDate(),
    endDate: moment().toDate(),
  }

  const classes = useStyles();
  const [filterDates, setFilterDates] = useState<ISalesActionDates>(defaultFilterDates);

  const [serverRecords, setServerRecords] = useState<ISalesRecord[]>([]);
  const [displayRows, setDisplayRows] = useState<ISalesDisplayRecord[]>([]);

  // The table rows and method to set them. This is a subset of displayRows, as it
  // is filtered by the collections filter
  const [rows, setRows] = useState<ISalesDisplayRecord[]>([]);

  // The product collections to allow filtering.
  const [filterCollections] = useState<string[]>(props.dataService.getCollectionsFilters());
  // The collection to filter.
  const [collectionFilter, setCollectionFilter] = useState<string>('All');

  const [columns, setColumns] = useState<Column[]>(props.dataService.buildDisplayColumns());
  const [defaultColumnWidths] = useState<TableColumnWidthInfo[]>(props.dataService.buildDisplayColumnWidthInfo());

  // Enable/disable the loading spinner.
  const [loading, setLoading] = React.useState(false);

  // Enable/disable the toolbar action buttons.
  const [actionsEnabled, setActionsEnabled] = React.useState(false);

  /**
   * Use custom hook to handle errors generated during report generation.
   */
  const errorHandler = useErrorHandler();

  /** 
   * Render the component when the filter dates change.
   */
  React.useEffect(() => {
    const effect = async () => {
        // tslint:disable-next-line:no-console
        // console.log('SalesReport - useEffect: ' + JSON.stringify(filterDates));

        setLoading(true);
        setActionsEnabled(false);

        try {
          const newRows: ISalesRecord[] = await props.dataService.retrieveSales(filterDates.startDate, filterDates.endDate);

          setServerRecords(newRows);

          const allDisplayRows = props.dataService.buildDisplayRows(newRows);
          setDisplayRows(allDisplayRows);

          const filteredRecords = filterRecords(collectionFilter, allDisplayRows)
          setRows(filteredRecords);
          setLoading(false);
          setActionsEnabled(true);
          // tslint:disable-next-line:no-console
          // console.log('SalesReport - refresh: ' + collectionFilter);
        }
        catch(e) {
          // tslint:disable-next-line:no-console
          // console.log('SalesReport - error: ', e);
           setLoading(false);
           setActionsEnabled(true);

           // Must throw the error in a new hook as I found that error boundary
           // doesn't always catch errors generated from async calls within
           // a hook.
           errorHandler(e);
        }
    }

    effect();

  }, [filterDates.startDate, filterDates.endDate])

  /**
   * Refresh the report data with the new date filters.
   * 
   * @param actionDates The transaction date filters.
   */
  const refreshHandler = (actionDates: ISalesActionDates) => {
    // tslint:disable-next-line:no-console
    // console.log('SalesReport - refreshHandler: ', actionDates);

    setFilterDates(actionDates);
  }
  
  /**
   * Export the report using the date filters
   * 
   * @param filterDates The sale's transaction date filters.
   */
  const exportHandler = async (exportFilterDates: ISalesActionDates) => {
    setLoading(true);
    setActionsEnabled(false);

    props.dataService.buildExportReport(serverRecords, exportFilterDates.startDate, exportFilterDates.endDate)
      .then(() => {
        setFilterDates(exportFilterDates);
        setLoading(false);
        setActionsEnabled(true);
      });
  }

  /**
   * Refresh the report data with the new collection filters.
   * 
   * @param actionDates The transaction date filters.
   */
  const collectionFilterHandler = (collection: string) => {
    setLoading(true);
    setActionsEnabled(false);
    setCollectionFilter(collection);
    
    setColumns(props.dataService.buildDisplayColumns());

    setRows(filterRecords(collection, displayRows));
    setLoading(false);
    setActionsEnabled(true);
  }
  
  /**
   * Filter sales records by the sale product's main collection.
   */
  const filterRecords = (collection: string, tableRows: ISalesDisplayRecord[]): ISalesDisplayRecord[] => {
    if (collection === 'All') { 
      return tableRows;
    }

    const filteredRows: ISalesDisplayRecord[] = [];

    tableRows.forEach(record => {
      if(record.collection && record.collection === collection) {
        filteredRows.push(record);
      }
    })

    return filteredRows;
  }

  /**
   * Wrap the toolbar component, adding the report filters and actions properties.
   */
  const ToolbarComponentWrapper: React.FC<Toolbar.RootProps> = (toolbarProps: Toolbar.RootProps) => {
    return (
      <SalesToolbar 
        actionsEnabled={actionsEnabled} 
        refreshAction={refreshHandler} 
        exportAction={exportHandler} 
        filterAction={collectionFilterHandler}
        collections={filterCollections}
        collectionFilter={collectionFilter}
        actionDates={filterDates}
        {...toolbarProps} 
      />
    )
  }
  
  /**
   * Build the report component.
   */
  return (
    <main className={classes.main}>
      <Grid
        columns={columns}
        rows={rows} 
      >
        <PagingState
          defaultCurrentPage={0}
          defaultPageSize={15}
        />
        <IntegratedPaging />
        <Table />
        <Toolbar
           rootComponent = {ToolbarComponentWrapper}
        />
        <TableColumnResizing defaultColumnWidths={defaultColumnWidths} />
        <TableHeaderRow 
          rowComponent={ReportHeaderRow}
        />
        <PagingPanel pageSizes={[15, 30, 100]} />
      </Grid>
      {loading && <CircularProgress size={80} disableShrink={true} className={classes.loadingProgress} />}
    </main>
  );
}

const useStyles = makeStyles((theme: Theme) => {
  // tslint:disable-next-line:no-console
  // console.log('theme: ', theme);
  return createStyles({
    main: {
      width: 'auto',
      display: 'block', // Fix IE 11 issue.
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
    },
    loadingProgress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    },
  })
});

export default withReportService<SalesReportService>(SalesReportBase, SalesReportService);
